<template>
  <div>
    <vxe-grid
      ref="table"
      size="small"
      row-id="rowKey"
      resizable
      auto-resize
      show-footer
      highlight-hover-row
      :loading="loading"
      :data="tableData"
      :columns="tableColumns"
      :toolbar-config="tableToolbar"
      :cell-class-name="cellClassName"
      :footer-method="() => footerData"
      :edit-config="{
        enabled: !disabled,
        trigger: 'click',
        mode: 'row',
        activeMethod: ({ row }) => !row.parentRowKey,
      }"
      :tree-config="{
        children: 'items',
        iconOpen: 'el-icon-arrow-down tool-btn',
        iconClose: 'el-icon-arrow-up tool-btn',
        indent: 0,
        expandAll: true,
        reserve: true,
      }"
      :seq-config="{ seqMethod }"
      :edit-rules="editRules"
      @edit-closed="editClosed"
    >
      <template v-slot:toolbar_form>
        <slot name="header"></slot>
      </template>

      <template v-slot:opt="{ row }">
        <i
          v-if="!row.parentRowKey"
          class="el-icon-delete tool-btn"
          @click="removeRow(row)"
        />
      </template>

      <template v-slot:displayImage="{ row }">
        <el-image
          v-if="row.evgSkuPlatformCode"
          :src="$osscdn(row.defaultImageUrl) || '/image/static/img/nopic.jpg'"
          :preview-src-list="[
            $osscdn(row.defaultImageUrl) || '/image/static/img/nopic.jpg',
          ]"
          style="width:60px; height:60px"
        >
          <div slot="error" class="image-slot">
            <img
              src="/image/static/img/nopic.jpg"
              style="width: 60px; height: 60px"
            />
          </div>
        </el-image>
      </template>

      <template v-slot:displayProductName="{ row }">
        <TableLabel :value="row.goodsName" />
        <TableLabel :value="row.goodsNameEn" />
        <TableLabel
          label="SKU"
          labelKey="table_labelKey_evgSkuNo"
          :value="row.evgSkuNo"
        />
      </template>

      <template v-slot:displaySpecColor="{ row }">
        <TableLabel :value="row.goodsSize" />
        <TableLabel
          :value="`${row.goodsColor || ''} ${row.goodsColorEn || ''}`"
        />
        <TableLabel :value="row.goodsPattern" />
      </template>

      <template v-slot:displayProductNo="{ row }">
        <TableLabel
          label="UPC"
          labelKey="table_labelKey_channelEanNo"
          :value="row.channelEanNo"
        />
        <TableLabel
          label="ITEM"
          labelKey="table_labelKey_channelItemNo"
          :value="row.channelItemNo"
        />
        <TableLabel
          label="NC"
          labelKey="table_labelKey_ncNo"
          :value="row.ncNo"
        />
      </template>

      <template v-slot:displayProductUnit="{ row }">
        <TableLabel
          label="单位"
          labelKey="label_key_unit"
          :value="row.v_skuPackSize"
        />
        <TableLabel
          label="入数"
          labelKey="label_key_qty"
          :value="row.specInUnitQty"
        />
      </template>

      <template v-slot:displayCartonType="{ row }">
        <TableLabel v-if="!row.parentRowKey" :value="row.v_cartonType" />
        <TableLabel
          label="入数"
          labelKey="label_key_qty"
          :value="row.unitInCtnQty"
        />
      </template>

      <template v-slot:ctnInPalletQtyInput="{ row }">
        <el-input-number
          v-if="!row.parentRowKey"
          v-model="row.ctnInPalletQty"
          :min="0"
          :step="1"
          :precision="0"
          :controls="false"
          style="width: 100%;"
          size="mini"
        />
      </template>

      <template v-slot:empty>
        <div style="text-align: center">
          <img src="/image/static/img/nodata.png" />
          <p class="no-data mt-1">
            {{ $l('INDEX.table_label_no_data') || '暂无数据！' }}
          </p>
        </div>
      </template>
    </vxe-grid>
  </div>
</template>
<script>
import TableAutoHeight from '@/mixins/table-auto-height'
import EditLoading from '@/mixins/edit-loading'
import { format, multiply, add } from '@/libs/util.number.js'
import tableColumns from './table-columns'
import TableLabel from './label.vue'
export default {
  components: { TableLabel },
  mixins: [TableAutoHeight, EditLoading],
  props: {
    tableData: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: true },
    inContainerQty: { type: Number, default: 0 },
  },
  data() {
    return {
      tableTotal: {},
      tableColumns: tableColumns(this),
      format,
      multiply,
      editRules: {
        ctnInPalletQty: [
          {
            validator: ({ row }) => {
              const { ctnInPalletQty, parentRowKey } = row
              if (parentRowKey) return

              if (!ctnInPalletQty && 0 != ctnInPalletQty) {
                return new Error(
                  this.$l('table_message_ctnInPalletQty') || '请输入入托箱数'
                )
              }

              if (0 == ctnInPalletQty) {
                return new Error(
                  this.$l('table_message_ctnInPalletQty_is0') ||
                    '入托箱数不能为0'
                )
              }
            },
          },
          {
            validator: ({ row }) => {
              const { specInUnitQty, parentRowKey, evgSkuPlatformCode } = row

              if (!evgSkuPlatformCode) return
              if (parentRowKey) return
              if (!specInUnitQty) {
                return new Error(
                  this.$l('table_message_specInUnitQty') ||
                    '该产品未填写规格信息，无法保存'
                )
              }
            },
          },
        ],
      },
    }
  },
  computed: {
    footerData() {
      const result = this.tableColumns
        .filter((c) => c.visible != false)
        .map((t) => {
          if (!t.isTotalColumn) return ''
          const val = this.tableTotal[t.field]
          return val ? format(val) : '0'
        })
      result[3] = this.$l('INDEX.total') || '合计'
      return [result]
    },
    tableToolbar() {
      return {
        slots: { buttons: 'toolbar_form' },
        refresh: this.disabled ? { query: () => this.$emit('reload') } : null,
        zoom: true,
        custom: true,
      }
    },
  },
  watch: {
    footerData() {
      this.$refs.table.updateFooter()
    },
    inContainerQty() {
      this.calcTable()
    },
  },
  methods: {
    cellClassName({ column }) {
      return column.property === 'col' ? '' : 'align-top'
    },
    removeRow(row) {
      this.$refs.table.remove(row)
      this.calcTotal()
    },
    async insertAt(records, row = -1, calc = false) {
      const result = await this.$refs.table.insertAt(records, row)
      if (calc) this.calcTable()
      return result
    },
    getTableData() {
      const data = this.$refs.table.getTableData()
      return data?.tableData || []
    },
    async validate() {
      const result = await this.$refs.table.validate(true).catch(() => true)
      return !result
    },
    setTreeExpand(rows, checked = true) {
      this.$refs.table.setTreeExpand(rows, checked)
    },
    setAllTreeExpand(checked = true) {
      this.$refs.table.setAllTreeExpand(checked)
    },
    seqMethod({ row }) {
      const tableData = this.getTableData()
      const { rowKey, parentRowKey } = row

      const getIndex = (key) => {
        return tableData.findIndex((item) => item.rowKey === key)
      }

      if (!parentRowKey) return getIndex(rowKey) + 1

      const parentRow = tableData.find((item) => item.rowKey === parentRowKey)
      const { items } = parentRow
      const parentRowIndex = getIndex(parentRowKey)
      const rowIndex = items.findIndex((item) => item.rowKey === rowKey)
      return `${parentRowIndex + 1}.${rowIndex + 1}`
    },
    editClosed({ row }) {
      this.calcRow(row)
      this.calcTotal()
    },
    calcTable() {
      const tableData = this.getTableData()
      tableData.forEach((row) => this.calcRow(row))
      this.calcTotal()
    },
    calcRow(row) {
      const { items, ctnInPalletQty } = row
      if (items?.length) {
        items.forEach((item) => {
          const { unitInCtnQty, specInUnitQty } = item
          const unitInPalletQty = multiply(unitInCtnQty, ctnInPalletQty)
          const specInPalletQty = multiply(unitInPalletQty, specInUnitQty)

          item.unitInPalletQty = unitInPalletQty
          item.specInPalletQty = specInPalletQty
          item.unitInContainerQty = multiply(
            unitInPalletQty,
            this.inContainerQty
          )
          item.specInContainerQty = multiply(
            specInPalletQty,
            this.inContainerQty
          )
        })

        const fields = [
          'unitInPalletQty',
          'specInPalletQty',
          'unitInContainerQty',
          'specInContainerQty',
        ]
        const total = items.reduce((result, next) => {
          fields.forEach((f) => (result[f] = add(result[f], next[f])))
          return result
        }, Object.fromEntries(fields.map((f) => [f, 0])))

        row.unitInPalletQty = total.unitInPalletQty
        row.specInPalletQty = total.specInPalletQty
        row.unitInContainerQty = total.unitInContainerQty
        row.specInContainerQty = total.specInContainerQty

        return
      }

      const { unitInCtnQty, specInUnitQty } = row
      const unitInPalletQty = multiply(unitInCtnQty, ctnInPalletQty)
      const specInPalletQty = multiply(unitInPalletQty, specInUnitQty)

      row.unitInPalletQty = unitInPalletQty
      row.specInPalletQty = specInPalletQty
      row.unitInContainerQty = multiply(unitInPalletQty, this.inContainerQty)
      row.specInContainerQty = multiply(specInPalletQty, this.inContainerQty)
    },
    calcTotal() {
      this.tableTotal = {}

      const tableData = this.getTableData()
      if (!tableData?.length) return

      const totalColumnFields = this.tableColumns
        .filter((c) => c.isTotalColumn)
        .map((c) => c.field)
      if (!totalColumnFields?.length) return

      this.tableTotal = tableData.reduce((result, next) => {
        totalColumnFields.forEach((f) => {
          result[f] = add(result[f], next[f])
        })
        return result
      }, Object.fromEntries(totalColumnFields.map((f) => [f, 0])))
    },
    clear() {
      this.$refs.table.reloadData([])
      this.calcTotal()
    },
  },
}
</script>
<style lang="scss" scoped>
::v-deep {
  .vxe-cell {
    .el-input-number {
      margin-top: 10px !important;
    }
  }
  .vxe-table--render-default .vxe-body--row:first-child .vxe-cell--valid {
    top: 50px;
  }
  .col--tree-node {
    .vxe-tree-cell {
      padding-left: 0;
    }
    .vxe-cell--tree-node {
      position: initial;
    }
    .vxe-tree--btn-wrapper {
      left: 0;
      right: 0;
      width: initial;
      height: initial;
      top: 24px;
      display: flex;
      justify-content: center;
      .vxe-tree--node-btn {
      }
    }
  }
}
</style>
